import { Controller } from "stimulus";
import dispatchCustomEvent from "../functions/dispatchCustomEvent.js";
import serializeForm from "../functions/serializeForm";

// Attached to a filter form: every `data-interval-reload-seconds` seconds (while the tab is
// visible) it re-dispatches the form's `data-event-name` with the form's fields as the query
// string — plus the page and limit currently shown by the target's pager — so the active filter
// and page survive the refresh. It then dispatches each event named in
// `data-interval-reload-extra-events` (space separated) without a query string.
//
// `refresh()` runs the same dispatch outside the timer — bind an element's success event to
// `<this-element-id>@window->interval-reload#refresh` (e.g. a form's own reload event, fired by
// ajax_success_controller with no detail) so that action reloads the filtered/paged target
// instead of the bare target, which would drop the filter and page.
export default class extends Controller {
  connect() {
    this.timer = setInterval(() => this.tick(), this.seconds * 1000);
  }

  disconnect() {
    clearInterval(this.timer);
  }

  tick() {
    if (document.visibilityState !== "visible") {
      return;
    }

    // A modal opened from a row in the reloaded target (e.g. delete/retry confirmation) would be
    // wiped out from under the user by the innerHTML replacement, so leave it alone until closed.
    if (document.querySelector(".modal.open")) {
      return;
    }

    this.dispatchReload({ silent: true });
  }

  refresh() {
    this.dispatchReload();
  }

  // Named to avoid shadowing Stimulus's own `Controller#dispatch` helper.
  dispatchReload(extraDetail = {}) {
    dispatchCustomEvent(document, this.eventName, true, true, {
      queryString: this.queryString,
      ...extraDetail,
    });

    this.extraEvents.forEach((eventName) => dispatchCustomEvent(document, eventName));
  }

  get queryString() {
    const params = new URLSearchParams(serializeForm(this.element));
    const pager = this.pager;

    // getAttribute rather than dataset: the double dash in `views--pager` defeats dataset's
    // camel-casing.
    if (pager) {
      params.set("page", pager.getAttribute("data-views--pager-page-value"));
      params.set("limit", pager.getAttribute("data-views--pager-limit-value"));
    }

    return params.toString();
  }

  // The pager rendered inside the reloadable target, if it has one right now.
  get pager() {
    const target = document.getElementById(this.eventName.replace("reload-", ""));
    return target ? target.querySelector("[data-controller~='views--pager']") : null;
  }

  get eventName() {
    return this.element.dataset.eventName;
  }

  get seconds() {
    return Number(this.element.dataset.intervalReloadSeconds) || 30;
  }

  get extraEvents() {
    return (this.element.dataset.intervalReloadExtraEvents || "").split(" ").filter(Boolean);
  }
}
